<template>
  <div class="viq-msg-assistant">
    <div class="viq-msg-assistant__avatar">
      <img src="@/assets/images/viq-avatar.png" alt="VIQ" width="24" height="24" />
    </div>

    <div class="viq-msg-assistant__content">
      <!-- Loading state (before answer) — inline bubble -->
      <div v-if="isStreaming && !message.content" class="viq-msg-assistant__loading-bubble">
        <ViqMessageLoading />
      </div>

      <!-- Answer card (renders once content exists) -->
      <div v-if="message.content" class="viq-msg-assistant__card">
        <div v-if="!isStreaming" class="viq-msg-assistant__card-header">
          <div class="viq-msg-assistant__card-header-left">
            <!-- Confidence badge hidden from clients but data still stored -->
            <!-- <ViqConfidenceBadge v-if="message.confidence" :level="message.confidence" /> -->
            <span v-if="citationsList.length > 0" class="viq-msg-assistant__source-count">
              {{ citationsList.length }} source{{ citationsList.length !== 1 ? 's' : '' }} identified
            </span>
          </div>
          <button
            class="viq-msg-assistant__print-btn"
            title="Download / Print"
            @click="printAnswer"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M12 9v3H2V9M7 1v8M4 5l3 3 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <div class="viq-msg-assistant__card-body">
          <div
            class="viq-msg-assistant__body"
            v-html="renderedContent"
            @click="handleContentClick"
          />

          <ViqFollowUps
            v-if="!isStreaming && isLast && isClarifyingResponse && quickReplies.length > 0"
            :suggestions="quickReplies"
            hide-header
          />

          <ViqFeedback
            v-if="!isStreaming && !isClarifyingResponse"
            :message-id="message.id || message.query_log_id"
          />
        </div>

        <div v-if="!isStreaming && citationsList.length > 0" class="viq-msg-assistant__sources">
          <div class="viq-msg-assistant__sources-header">
            {{ citationsList.length }} source{{ citationsList.length !== 1 ? 's' : '' }} cited
            <el-tooltip effect="dark" placement="top" :open-delay="300">
              <div slot="content">The specific sources used in this answer.<br>Click a source to view its full content.</div>
              <span class="viq-msg-assistant__help-icon">?</span>
            </el-tooltip>
          </div>
          <div class="viq-msg-assistant__sources-list">
            <div
              v-for="c in citationsList"
              :key="c.id"
              class="viq-msg-assistant__source-card"
              @click="openCitation(c)"
            >
              <div class="viq-msg-assistant__source-badges">
                <span
                  class="viq-msg-assistant__source-badge"
                  :class="sourceTypeClass(c.source_type)"
                >{{ formatSourceType(c.source_type) }}</span>
                <span
                  v-if="c.content_type"
                  class="viq-msg-assistant__source-badge viq-msg-assistant__source-badge--type"
                >{{ formatContentType(c.content_type) }}</span>
                <span
                  v-if="c.classification"
                  class="viq-msg-assistant__source-badge"
                  :class="classificationClass(c.classification)"
                >{{ c.classification }}</span>
              </div>
              <div class="viq-msg-assistant__source-title">{{ c.title }}</div>
              <div v-if="c.jurisdiction" class="viq-msg-assistant__source-meta">
                {{ c.jurisdiction }}
              </div>
            </div>
          </div>

          <ViqExpertCommentary
            v-if="!isStreaming && message.expert_commentary && message.expert_commentary.length > 0"
            :articles="message.expert_commentary"
          />

          <ViqUpsellJurisdictions
            v-if="!isStreaming && upsellJurisdictions.length > 0"
            :jurisdictions="upsellJurisdictions"
            :context="message.content ? message.content.substring(0, 200) : ''"
          />

          <ViqFollowUps
            v-if="!isStreaming && isLast && followUps.length > 0"
            :suggestions="followUps"
          />
        </div>
      </div>

      <ViqActionChips
        v-if="!isStreaming && isLast && message.content"
        :message="message"
      />
    </div>
  </div>
</template>

<script>
import DOMPurify from "dompurify";

export default {
  name: "ViqMessageAssistant",
  components: {
    // ViqConfidenceBadge: () => import("./ViqConfidenceBadge"),
    ViqMessageLoading: () => import("./ViqMessageLoading"),
    ViqExpertCommentary: () => import("./ViqExpertCommentary"),
    ViqFollowUps: () => import("./ViqFollowUps"),
    ViqUpsellJurisdictions: () => import("./ViqUpsellJurisdictions"),
    ViqActionChips: () => import("./ViqActionChips"),
    ViqFeedback: () => import("./ViqFeedback"),
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    citationsList() {
      return this.message.citations || [];
    },
    citationMap() {
      const map = {};
      (this.message.citations || []).forEach((c) => {
        map[c.id] = c;
      });
      return map;
    },
    upsellJurisdictions() {
      return this.message.upsell_jurisdictions || [];
    },
    followUps() {
      const fq = this.message.follow_up_questions;
      const fu = this.message.follow_ups;
      // Prefer whichever has actual items (empty arrays are truthy so check length)
      if (fq && fq.length > 0) return fq;
      if (fu && fu.length > 0) return fu;
      return [];
    },
    suggestionPills() {
      // Combine follow-ups and quick replies, deduplicated
      const all = [...this.followUps, ...this.quickReplies];
      const seen = new Set();
      return all.filter((item) => {
        const text = (typeof item === "string" ? item : item.text || "").toLowerCase().trim();
        if (!text || seen.has(text)) return false;
        seen.add(text);
        return true;
      }).slice(0, 4);
    },
    quickReplies() {
      let replies = this.message.quick_replies || [];

      // If no explicit quick_replies but VIQ is asking a clarifying question,
      // extract short options from the answer or use follow-ups as pills
      if (replies.length === 0 && this.isClarifyingResponse) {
        const followUps = this.message.follow_up_questions || this.message.follow_ups || [];
        // Use follow-ups as quick replies, shortened to key phrase
        replies = followUps.map((q) => {
          // Extract the core topic — take text before "?" and simplify
          const short = q.replace(/\?$/, "").replace(/^(What|How|Which|Compare|Explain)\s+(are|is|does|do|did|the|a)\s+/i, "");
          return short.length > 50 ? short.substring(0, 47) + "..." : short;
        });
      }

      // Deduplicate (case-insensitive)
      const seen = new Set();
      return replies.filter((r) => {
        const key = r.toLowerCase().trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, 3);
    },
    isClarifyingResponse() {
      const content = (this.message.content || "").toLowerCase();
      const citations = this.message.citations || [];
      // It's a clarifying response if: short answer, ends with a question, and few/no citations
      return citations.length <= 1 &&
        content.length < 500 &&
        /\?\s*$/.test(content.trim());
    },
    renderedContent() {
      const raw = this.message.content || "";
      if (!raw) return "";
      // Access citationMap here so Vue 2 tracks it as a dependency
      const citMap = this.citationMap;
      const html = this.renderMarkdown(raw, citMap);
      return DOMPurify.sanitize(html, {
        ADD_ATTR: ["data-cite-id", "style", "title", "role", "tabindex"],
        ADD_TAGS: ["table", "thead", "tbody", "tr", "th", "td"],
      });
    },
  },
  methods: {
    printAnswer() {
      const cardBody = this.$el.querySelector(".viq-msg-assistant__card-body");
      if (!cardBody) return;
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;left:-9999px;width:0;height:0;border:none;";
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`<!DOCTYPE html><html><head><title>VIQ Answer</title><style>
        body { font-family: 'Proxima Nova', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 32px; color: #111827; line-height: 1.7; font-size: 14px; }
        h1,h2,h3 { margin: 16px 0 8px; font-weight: 700; }
        table { width: 100%; border-collapse: collapse; margin: 12px 0; }
        th, td { padding: 8px 12px; border: 1px solid #e5e7eb; text-align: left; }
        th { background: #f1f5f9; font-weight: 600; }
        ul, ol { padding-left: 1.5em; }
        code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 0.875em; }
      </style></head><body>${cardBody.innerHTML}</body></html>`);
      doc.close();
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    },
    handleContentClick(e) {
      const pill = e.target.closest("[data-cite-id]");
      if (!pill) return;
      const id = pill.getAttribute("data-cite-id");
      const citation = this.citationMap[id];
      if (citation) {
        this.openCitation(citation);
      }
    },
    askQuickReply(reply) {
      this.$store.dispatch("Viq/askQuestion", {
        question: reply,
        conversationId: this.$store.state.Viq.activeConversation?.id || null,
        lensId: this.$store.state.Viq.activeLens?.id || null,
      });
    },
    openCitation(citation) {
      const excerpts = citation.excerpts || [];
      const hasExcerpts = excerpts.length > 0 || !!(citation.excerpt);
      this.$store.dispatch("Viq/openSlideOut", {
        id: citation.id,
        title: citation.title,
        type: this.formatContentType(citation.content_type || citation.type),
        jurisdiction: citation.jurisdiction,
        content: hasExcerpts ? "" : (citation.summary || ""),
        excerpt: citation.excerpt || "",
        excerpts,
        content_summary: citation.summary || "",
        url: this.localUrl(citation.platform_url),
        source_type: citation.source_type,
        classification: citation.classification,
        vertical: citation.vertical,
        tier: citation.tier,
        published_date: citation.published_date,
        last_updated: citation.last_updated,
      });
    },
    sourceTypeClass(sourceType) {
      const map = {
        analyst: "viq-msg-assistant__source-badge--analyst",
        ai_generated: "viq-msg-assistant__source-badge--ai",
        legislation: "viq-msg-assistant__source-badge--legislation",
      };
      return map[sourceType] || "";
    },
    classificationClass(classification) {
      const map = {
        Actionable: "viq-msg-assistant__source-badge--actionable",
        Indicative: "viq-msg-assistant__source-badge--indicative",
        Informative: "viq-msg-assistant__source-badge--informative",
      };
      return map[classification] || "";
    },
    formatSourceType(type) {
      const map = {
        analyst: "Analyst",
        ai_generated: "AI",
        legislation: "Legislation",
      };
      return map[type] || type || "";
    },
    localUrl(url) {
      if (!url) return "/";
      try {
        const parsed = new URL(url);
        return parsed.pathname + parsed.search + parsed.hash;
      } catch (e) {
        return url;
      }
    },
    formatContentType(type) {
      if (!type) return "";
      return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    renderMarkdown(text, citMap) {
      let html = text;

      // Preserve HTML blocks before escaping — Skywalker returns pre-formatted tables
      // Match complete tables AND partial tables (during streaming)
      const preservedBlocks = [];
      html = html.replace(/<table[\s\S]*?(?:<\/table>|$)/gi, (match) => {
        preservedBlocks.push(match);
        return `\n%%BLOCK_${preservedBlocks.length - 1}%%\n`;
      });

      // Escape HTML (now safe since table HTML is preserved)
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

      // Headers
      html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
      html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
      html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");

      // Bold and italic
      html = html.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

      // Inline code
      html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

      // Citation pills — match any [id] where the id exists in our citation map
      citMap = citMap || {};
      html = html.replace(/\[([a-z][\w-]*(?:-[\w]+)*)\]/gi, (match, id) => {
        const citation = citMap[id];
        if (citation) {
          const sourceClass = this.sourceTypeClass(citation.source_type);
          return `<span class="viq-cite-pill ${sourceClass}" data-cite-id="${id}" title="${(citation.title || "").replace(/"/g, "&quot;")}" role="button" tabindex="0">${id}</span>`;
        }
        return match;
      });

      // Unordered lists — convert consecutive `- ` or `* ` lines
      html = html.replace(/(?:^|\n)([*-] .+(?:\n[*-] .+)*)/g, (block) => {
        const items = block.trim().split("\n").map((l) => "<li>" + l.replace(/^[*-] /, "") + "</li>").join("");
        return "\n<ul>" + items + "</ul>\n";
      });

      // Ordered lists — convert consecutive `1. ` lines
      html = html.replace(/(?:^|\n)(\d+\. .+(?:\n\d+\. .+)*)/g, (block) => {
        const items = block.trim().split("\n").map((l) => "<li>" + l.replace(/^\d+\. /, "") + "</li>").join("");
        return "\n<ol>" + items + "</ol>\n";
      });

      // Tables — match markdown pipe tables
      html = html.replace(/(?:^|\n)(\|.+\|[ ]*\n\|[-| :]+\|[ ]*\n(?:\|.+\|[ ]*\n?)+)/g, (block) => {
        const lines = block.trim().split("\n").filter((l) => l.trim());
        if (lines.length < 2) return block;

        const parseRow = (line) =>
          line.split("|").slice(1, -1).map((cell) => cell.trim());

        const headers = parseRow(lines[0]);
        // lines[1] is the separator row (|---|---|)
        const alignments = parseRow(lines[1]).map((sep) => {
          if (sep.startsWith(":") && sep.endsWith(":")) return "center";
          if (sep.endsWith(":")) return "right";
          return "left";
        });

        let table = "<table><thead><tr>";
        headers.forEach((h, i) => {
          const align = alignments[i] ? ` style="text-align:${alignments[i]}"` : "";
          table += `<th${align}>${h}</th>`;
        });
        table += "</tr></thead><tbody>";

        for (let r = 2; r < lines.length; r++) {
          const cells = parseRow(lines[r]);
          table += "<tr>";
          cells.forEach((c, i) => {
            const align = alignments[i] ? ` style="text-align:${alignments[i]}"` : "";
            table += `<td${align}>${c}</td>`;
          });
          table += "</tr>";
        }
        table += "</tbody></table>";
        return "\n" + table + "\n";
      });

      // Horizontal rule
      html = html.replace(/^---$/gm, "<hr>");

      // Blockquotes
      html = html.replace(/(?:^|\n)((?:&gt; .+\n?)+)/g, (block) => {
        const text = block.trim().split("\n").map((l) => l.replace(/^&gt; ?/, "")).join("<br>");
        return "\n<blockquote>" + text + "</blockquote>\n";
      });

      // Trim whitespace around block-level elements before paragraph wrapping
      const blocks = "ul|ol|h[1-3]|hr|blockquote|table";
      const blockRe = new RegExp(`\\n*(<(?:${blocks})(?:>|\\s))`, "g");
      const blockEndRe = new RegExp(`(<\\/(?:${blocks})>)\\n*`, "g");
      html = html.replace(blockRe, "\n\n$1");
      html = html.replace(blockEndRe, "$1\n\n");

      // Collapse triple+ newlines to double
      html = html.replace(/\n{3,}/g, "\n\n");

      // Paragraphs (double newline)
      html = html.replace(/\n\n/g, "</p><p>");
      html = "<p>" + html + "</p>";

      // Strip p tags wrapping block elements
      html = html.replace(new RegExp(`<p>(<(?:${blocks})(?:>|\\s))`, "g"), "$1");
      html = html.replace(new RegExp(`(<\\/(?:${blocks})>)<\\/p>`, "g"), "$1");
      html = html.replace(/<p>(<hr>)<\/p>/g, "$1");
      html = html.replace(/<p><\/p>/g, "");

      // Line breaks (single newline within paragraphs)
      html = html.replace(/\n/g, "<br>");

      // Final cleanup — no <br> immediately after block close or before block open
      html = html.replace(new RegExp(`<br>\\s*(<(?:${blocks})(?:>|\\s))`, "g"), "$1");
      html = html.replace(new RegExp(`(<\\/(?:${blocks})>)\\s*<br>`, "g"), "$1");
      // No <br> at the start of a <p>
      html = html.replace(/<p><br>/g, "<p>");

      // Restore preserved HTML blocks (tables)
      preservedBlocks.forEach((block, i) => {
        // If it's an incomplete table (no closing tag), close it so the browser renders what we have
        let safeBlock = block;
        if (!/<\/table>/i.test(safeBlock)) {
          // Close any open tags so partial tables render
          const openTags = [];
          safeBlock.replace(/<(thead|tbody|tr|th|td)[\s>]/gi, (_, tag) => openTags.push(tag.toLowerCase()));
          safeBlock.replace(/<\/(thead|tbody|tr|th|td)>/gi, (_, tag) => {
            const idx = openTags.lastIndexOf(tag.toLowerCase());
            if (idx !== -1) openTags.splice(idx, 1);
          });
          // Close remaining open tags in reverse
          for (let t = openTags.length - 1; t >= 0; t--) {
            safeBlock += `</${openTags[t]}>`;
          }
          safeBlock += "</table>";
        }
        html = html.replace(`%%BLOCK_${i}%%`, safeBlock);
      });

      return html;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-msg-assistant {
  display: flex;
  align-items: flex-start;
  gap: $viq-space-sm;
  padding: $viq-space-sm 0;
  width: 100%;

  &__avatar {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 28px;
      height: 28px;
      object-fit: contain;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__loading-bubble {
    display: inline-block;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-lg;
    box-shadow: $viq-shadow-sm;
    padding: $viq-space-sm $viq-space-md;
  }

  &__card {
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-lg;
    box-shadow: $viq-shadow-sm;
    overflow: hidden;
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $viq-space-sm $viq-space-lg;
    border-bottom: 1px solid $viq-border;
    background: $viq-bg-secondary;
  }

  &__card-header-left {
    display: flex;
    align-items: center;
    gap: $viq-space-md;
  }

  &__print-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    color: $viq-text-secondary;
    cursor: pointer;
    border-radius: $viq-radius-sm;
    transition: background $viq-transition-fast, color $viq-transition-fast;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      color: $viq-text-primary;
    }
  }

  &__source-count {
    font-size: $viq-font-xs;
    font-weight: 600;
    color: $viq-text-secondary;
  }

  &__card-body {
    padding: $viq-space-lg;
  }

  &__body {
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;
    word-break: break-word;

    ::v-deep {
      h1, h2, h3 {
        margin: $viq-space-lg 0 $viq-space-sm;
        font-weight: 700;
        color: $viq-text-primary;
        line-height: 1.3;
      }
      h1 { font-size: 1.5rem; }
      h2 { font-size: $viq-font-lg; }
      h3 { font-size: $viq-font-md; }

      p {
        margin: 0 0 $viq-space-sm;

        &:last-child {
          margin-bottom: 0;
        }
      }

      ul, ol {
        padding-left: 1.5em;
        margin: $viq-space-xs 0 $viq-space-sm;
      }

      ul {
        list-style-type: disc;

        ul {
          list-style-type: circle;
          margin: $viq-space-xs 0;
        }
      }

      ol {
        list-style-type: decimal;

        ol {
          list-style-type: lower-alpha;
          margin: $viq-space-xs 0;
        }
      }

      li {
        margin-bottom: 2px;
        padding-left: 0.25em;
        line-height: 1.6;

        &:last-child {
          margin-bottom: 0;
        }

        &::marker {
          color: $viq-text-secondary;
        }
      }

      code {
        background: #f1f5f9;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.875em;
        font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
        color: #1e293b;
      }

      pre {
        background: #1e293b;
        color: #e2e8f0;
        padding: $viq-space-md;
        border-radius: $viq-radius-md;
        overflow-x: auto;
        margin: $viq-space-sm 0 $viq-space-md;
        font-size: 0.875em;
        line-height: 1.5;

        code {
          background: none;
          padding: 0;
          color: inherit;
        }
      }

      strong {
        font-weight: 700;
        color: $viq-text-primary;
      }

      em {
        font-style: italic;
      }

      blockquote {
        border-left: 3px solid $viq-accent;
        margin: $viq-space-sm 0 $viq-space-md;
        padding: $viq-space-sm $viq-space-md;
        background: rgba(37, 99, 235, 0.04);
        border-radius: 0 $viq-radius-sm $viq-radius-sm 0;
        color: $viq-text-secondary;
        font-style: italic;
      }

      hr {
        border: none;
        border-top: 1px solid $viq-border;
        margin: $viq-space-md 0;
      }

      a {
        color: $viq-accent;
        text-decoration: underline;
        text-underline-offset: 2px;

        &:hover {
          color: $viq-accent-hover;
        }
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin: $viq-space-md 0;
        font-size: 1em;
        line-height: 1.6;
        border: 1px solid $viq-border;

        th, td {
          padding: 10px 14px;
          border: 1px solid $viq-border;
          text-align: left;
          vertical-align: top;
          color: $viq-text-primary;
          font-size: 1em;
        }

        th {
          background: #f1f5f9;
          font-weight: 700;
          font-size: 1em;
        }

        tbody tr:nth-child(even) {
          background: #fafbfc;
        }

        tbody tr:hover td {
          background: #f1f5f9;
        }

        td:first-child {
          font-weight: 600;
        }

        // Ensure bold/citation pills inside cells render
        strong {
          font-weight: 700;
        }

        .viq-cite-pill {
          font-size: 10px;
          padding: 0 5px;
        }
      }

      // Citation pills
      .viq-cite-pill {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        font-family: monospace;
        font-size: 11px;
        font-weight: 600;
        padding: 1px 7px;
        border-radius: 999px;
        border: none;
        cursor: pointer;
        vertical-align: baseline;
        margin: 0 2px;
        transition: opacity 0.15s;
        background: #dbeafe;
        color: #1d4ed8;

        &:hover {
          opacity: 0.75;
        }

        &.viq-msg-assistant__source-badge--analyst {
          background: #dbeafe;
          color: #1d4ed8;
        }
        &.viq-msg-assistant__source-badge--ai {
          background: #ede9fe;
          color: #6d28d9;
        }
        &.viq-msg-assistant__source-badge--legislation {
          background: #f1f5f9;
          color: #475569;
        }
      }
    }
  }

  // Quick replies — prominent refinement chips
  &__quick-replies {
    display: flex;
    flex-wrap: wrap;
    gap: $viq-space-sm;
    margin-top: $viq-space-sm;
    margin-bottom: $viq-space-sm;
  }

  &__quick-reply {
    padding: $viq-space-sm $viq-space-lg;
    background: $viq-bg-primary;
    border: 1.5px solid $viq-accent;
    border-radius: $viq-radius-full;
    font-size: $viq-font-sm;
    font-weight: 600;
    color: $viq-accent;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;
    white-space: nowrap;

    &:hover {
      background: $viq-accent;
      color: #fff;
      box-shadow: $viq-shadow-sm;
    }
  }

  // Sources section (inside card)
  &__sources {
    border-top: 1px solid $viq-border;
    padding: $viq-space-md $viq-space-lg;
    background: $viq-bg-secondary;
  }

  &__sources-header {
    display: flex;
    align-items: center;
    gap: $viq-space-xs;
    font-size: $viq-font-xs;
    font-weight: 600;
    color: $viq-text-secondary;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: $viq-space-sm;
  }

  &__sources-list {
    display: flex;
    flex-direction: column;
    gap: $viq-space-xs;
  }

  &__source-card {
    padding: $viq-space-sm $viq-space-md;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover {
      background: $viq-bg-secondary;
      box-shadow: $viq-shadow-sm;
    }
  }

  &__source-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
  }

  &__source-badge {
    display: inline-flex;
    padding: 1px 6px;
    border-radius: $viq-radius-sm;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    background: #f1f5f9;
    color: #475569;

    &--analyst {
      background: #dbeafe;
      color: #1d4ed8;
    }
    &--ai {
      background: #ede9fe;
      color: #6d28d9;
    }
    &--legislation {
      background: #f1f5f9;
      color: #475569;
    }
    &--type {
      background: #f3f4f6;
      color: #6b7280;
    }
    &--actionable {
      background: #fee2e2;
      color: #b91c1c;
    }
    &--indicative {
      background: #fef3c7;
      color: #92400e;
    }
    &--informative {
      background: #e0f2fe;
      color: #0369a1;
    }
  }

  &__source-title {
    font-size: $viq-font-sm;
    font-weight: 600;
    color: $viq-text-primary;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__source-meta {
    font-size: $viq-font-xs;
    color: $viq-text-muted;
    margin-top: 2px;
  }
}
</style>

<!-- Unscoped styles for v-html rendered content (scoped styles don't penetrate v-html in Vue 2) -->
<style lang="scss">
@import "@/assets/styles/viq.scss";

.viq-msg-assistant__body {

  // Force all v-html content to match body font size
  table, th, td, p, li, ul, ol, blockquote, code, strong, em, a, span, h1, h2, h3 {
    font-family: inherit;
  }

  // Citation pills
  .viq-cite-pill {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-family: monospace;
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    vertical-align: baseline;
    margin: 0 2px;
    transition: opacity 0.15s;
    background: #dbeafe;
    color: #1d4ed8;

    &:hover {
      opacity: 0.75;
    }

    &.viq-msg-assistant__source-badge--analyst {
      background: #dbeafe;
      color: #1d4ed8;
    }
    &.viq-msg-assistant__source-badge--ai {
      background: #ede9fe;
      color: #6d28d9;
    }
    &.viq-msg-assistant__source-badge--legislation {
      background: #f1f5f9;
      color: #475569;
    }
  }

  // Tables — use same size as parent .viq-msg-assistant__body
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: inherit !important;
    line-height: 1.6;
    border: 1px solid #e5e7eb;

    th, td {
      padding: 10px 14px;
      border: 1px solid #e5e7eb;
      text-align: left;
      vertical-align: top;
      font-size: inherit !important;
    }

    th {
      background: #f1f5f9;
      font-weight: 700;
    }

    tbody tr:nth-child(even) {
      background: #fafbfc;
    }

    tbody tr:hover td {
      background: #f1f5f9;
    }

    td:first-child {
      font-weight: 600;
    }

    .viq-cite-pill {
      font-size: 10px !important;
      padding: 0 5px;
    }
  }
}
</style>
